/* ==========================================================================
   Home Page Component (Full Multi-Language Support)
   With Tagline "Saving lives, with a speed of sound!", Verified Images,
   7x Academic Citation Link, Refined Pictograms & Visual Architecture Graph
   ========================================================================== */

import { t, getLocale } from '../locales/i18n.js';
import { renderLiveIncidentsWidget } from '../components/LiveIncidentsWidget.js';
import { renderCustomerCarousel } from '../components/CustomerCarousel.js';
import { renderTestimonialsCarousel } from '../components/TestimonialsCarousel.js';
import { renderArchitectureGraph } from '../components/ArchitectureGraph.js';
import { openArticleModal } from '../components/ArticleModal.js';
import { getArticlesByCategory, subscribeArticleStore } from '../services/articleStore.js';
import { articleIndexTranslations } from '../locales/articleTranslationsData.js';
import { getDetectionSvg } from '../components/DetectionIcons.js';

export function renderHomePage(onNavigate) {
  const container = document.createElement('div');
  container.className = 'sed-page';

  // 1. Hero Section
  const heroSection = document.createElement('section');
  heroSection.className = 'sed-hero';
  heroSection.innerHTML = `
    <div class="sed-container">
      <div class="sed-hero-grid">
        <div>
          <div class="sed-eyebrow" style="background: rgba(53, 168, 223, 0.12); color: var(--blue-primary); padding: 0.45rem 1.1rem; border-radius: 20px; font-weight: 800; font-size: 0.88rem; letter-spacing: 0.5px; border: 1px solid rgba(53, 168, 223, 0.25); display: inline-flex; align-items: center; gap: 8px; margin-bottom: 1.25rem;">
            <span class="sed-eyebrow-dot"></span>
            ${t('home.heroBadge')}
          </div>

          <h1 class="sed-hero-title">
            ${t('home.heroTitleLine1') || 'The camera sees.'}<br>
            <span>${t('home.heroTitleLine2') || 'SED hears.'}</span>
          </h1>

          <p class="sed-hero-subtitle">
            ${t('home.heroSubtitle')}
          </p>

          <div class="sed-hero-cta">
            <a href="#solutions" class="sed-btn sed-btn-hero sed-btn-primary" data-route="solutions">${t('home.exploreSolutions')}</a>
            <a href="#contact" class="sed-btn sed-btn-hero sed-btn-outline" style="color: var(--blue-primary); border-color: var(--blue-primary);" data-route="contact">${t('home.requestConsultation')}</a>
          </div>
        </div>

        <div id="sed-radar-container">
          <!-- Live Incidents Widget Appended Here -->
        </div>
      </div>
    </div>
  `;

  const radarSlot = heroSection.querySelector('#sed-radar-container');
  if (radarSlot) {
    const radarNode = renderLiveIncidentsWidget();
    if (radarNode && radarNode instanceof Node) {
      radarSlot.appendChild(radarNode);
    }
  }

  container.appendChild(heroSection);

  // 2. Featured Video Demonstrations (YouTube Embeds)
  const videoSection = document.createElement('section');
  videoSection.className = 'sed-section';
  videoSection.style.backgroundColor = '#ffffff';
  videoSection.innerHTML = `
    <div class="sed-container">
      <div class="sed-section-header">
        <div class="sed-eyebrow">${t('home.videoEyebrow') || 'REAL-WORLD DEMONSTRATIONS'}</div>
        <h2 class="sed-section-title">${t('home.seeInAction')}</h2>
        <p class="sed-section-desc">${t('home.watchFieldDemo')}</p>
      </div>

      <div class="sed-grid-2">
        <div class="sed-card" style="padding: 1.25rem; display: flex; flex-direction: column; justify-content: space-between;">
          <div>
            <div style="position: relative; padding-bottom: 56.25%; height: 0; overflow: hidden; border-radius: var(--radius-unified); border: 1px solid var(--slate-line); margin-bottom: 1.25rem;">
              <iframe src="https://www.youtube.com/embed/1ZbI0-uAkjA" title="${t('home.videoCtTitle')}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen style="position: absolute; top:0; left:0; width:100%; height:100%;"></iframe>
            </div>
            <h3 class="sed-card-title">
              <a href="https://youtu.be/1ZbI0-uAkjA?si=FV_Z993s5by7pa8f" target="_blank" rel="noopener noreferrer" style="color: var(--navy-dark); text-decoration: none;" onmouseover="this.style.color='var(--blue-primary)'" onmouseout="this.style.color='var(--navy-dark)'">
                ${t('home.videoCtTitle')}
              </a>
            </h3>
            <p class="sed-card-text">${t('home.videoCtDesc')}</p>
          </div>
          <div style="display: flex; justify-content: space-between; align-items: center; border-top: 1px solid var(--slate-line); padding-top: 0.85rem; margin-top: 1rem;">
            <span style="font-size: 0.78rem; font-weight: 800; color: var(--blue-primary); text-transform: uppercase;">${t('home.videoCtTag')}</span>
            <a href="https://youtu.be/1ZbI0-uAkjA?si=FV_Z993s5by7pa8f" target="_blank" rel="noopener noreferrer" class="sed-btn sed-btn-outline" style="font-size: 0.8rem; font-weight: 700; padding: 5px 12px; text-decoration: none; display: inline-flex; align-items: center; gap: 4px;">
              <span>${t('home.watchYt')}</span>
            </a>
          </div>
        </div>

        <div class="sed-card" style="padding: 1.25rem; display: flex; flex-direction: column; justify-content: space-between;">
          <div>
            <div style="position: relative; padding-bottom: 56.25%; height: 0; overflow: hidden; border-radius: var(--radius-unified); border: 1px solid var(--slate-line); margin-bottom: 1.25rem;">
              <iframe src="https://www.youtube.com/embed/PvwzwjHTcN8" title="${t('home.videoGunTitle')}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen style="position: absolute; top:0; left:0; width:100%; height:100%;"></iframe>
            </div>
            <h3 class="sed-card-title">
              <a href="https://www.youtube.com/watch?v=PvwzwjHTcN8" target="_blank" rel="noopener noreferrer" style="color: var(--navy-dark); text-decoration: none;" onmouseover="this.style.color='var(--blue-primary)'" onmouseout="this.style.color='var(--navy-dark)'">
                ${t('home.videoGunTitle')}
              </a>
            </h3>
            <p class="sed-card-text">${t('home.videoGunDesc')}</p>
          </div>
          <div style="display: flex; justify-content: space-between; align-items: center; border-top: 1px solid var(--slate-line); padding-top: 0.85rem; margin-top: 1rem;">
            <span style="font-size: 0.78rem; font-weight: 800; color: var(--blue-primary); text-transform: uppercase;">${t('home.videoGunTag')}</span>
            <a href="https://www.youtube.com/watch?v=PvwzwjHTcN8" target="_blank" rel="noopener noreferrer" class="sed-btn sed-btn-outline" style="font-size: 0.8rem; font-weight: 700; padding: 5px 12px; text-decoration: none; display: inline-flex; align-items: center; gap: 4px;">
              <span>${t('home.watchYt')}</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  `;
  container.appendChild(videoSection);

  // 3. Value Proposition Section
  const valuePropSection = document.createElement('section');
  valuePropSection.className = 'sed-section-alt';
  valuePropSection.innerHTML = `
    <div class="sed-container">
      <div class="sed-section-header">
        <div class="sed-eyebrow">${t('home.acousticAdvantageEyebrow') || 'ACOUSTIC ADVANTAGE'}</div>
        <h2 class="sed-section-title">${t('home.blindSpotsTitle')}</h2>
        <p class="sed-section-desc">${t('home.blindSpotsLead')}</p>
      </div>

      <div class="sed-grid-3">
        <!-- 7x Faster Card -->
        <div class="sed-card" style="display: flex; flex-direction: column; justify-content: space-between;">
          <div>
            <div style="width: 54px; height: 54px; background: rgba(53, 168, 223, 0.1); border-radius: 12px; display: flex; align-items: center; justify-content: center; margin-bottom: 1.25rem;">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--blue-primary)" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
              </svg>
            </div>
            <h3 class="sed-card-title">${t('home.statResponse')}</h3>
            <p class="sed-card-text">${t('home.statResponseDesc')}</p>
          </div>
          
          <div style="border-top: 1px solid var(--slate-line); margin-top: 1.25rem; padding-top: 0.85rem;">
            <a href="https://iieta.org/journals/ijsse/paper/10.18280/ijsse.150413" target="_blank" rel="noopener noreferrer" style="font-size: 0.82rem; font-weight: 700; color: var(--blue-primary); text-decoration: none; display: inline-flex; align-items: center; gap: 4px;">
              📄 Evidence Study: IIETA IJSSI Paper →
            </a>
          </div>
        </div>

        <!-- 0 New Cameras Card -->
        <div class="sed-card">
          <div style="width: 54px; height: 54px; background: rgba(53, 168, 223, 0.1); border-radius: 12px; display: flex; align-items: center; justify-content: center; margin-bottom: 1.25rem;">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--blue-primary)" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
              <circle cx="12" cy="13" r="4"></circle>
            </svg>
          </div>
          <h3 class="sed-card-title">${t('home.statCameras')}</h3>
          <p class="sed-card-text">${t('home.statCamerasDesc')}</p>
        </div>

        <!-- 100% Privacy Shield Card -->
        <div class="sed-card">
          <div style="width: 54px; height: 54px; background: rgba(53, 168, 223, 0.1); border-radius: 12px; display: flex; align-items: center; justify-content: center; margin-bottom: 1.25rem;">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--blue-primary)" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
              <path d="M9 12l2 2 4-4"></path>
            </svg>
          </div>
          <h3 class="sed-card-title">${t('home.statPrivacy')}</h3>
          <p class="sed-card-text">${t('home.statPrivacyDesc')}</p>
        </div>
      </div>
    </div>
  `;
  container.appendChild(valuePropSection);

  // 4. Acoustic Detections Catalog Section
  const detectionCatalogSection = document.createElement('section');
  detectionCatalogSection.className = 'sed-section';
  detectionCatalogSection.style.backgroundColor = '#ffffff';
  detectionCatalogSection.innerHTML = `
    <div class="sed-container">
      <div class="sed-section-header">
        <div class="sed-eyebrow">${t('home.comprehensiveCoverageEyebrow') || 'COMPREHENSIVE ACOUSTIC COVERAGE'}</div>
        <h2 class="sed-section-title">${t('home.threatsTitle')}</h2>
        <p class="sed-section-desc">${t('home.threatsSubtitle')}</p>
      </div>

      <div class="sed-grid-4">
        <!-- Gunshot -->
        <div class="sed-card" style="padding: 0; overflow: hidden; cursor: pointer; display: flex; flex-direction: column; justify-content: space-between;" data-route="detection-gunshot">
          <div>
            <div style="height: 180px; background: #f8fafc; padding: 1rem; text-align: center; border-bottom: 1px solid var(--slate-line); display: flex; align-items: center; justify-content: center;">
              <img src="/images/Detekce_Strelba.png" alt="${t('detectionTypes.gunshot')}" style="max-width: 100%; max-height: 160px; object-fit: contain;">
            </div>
            <div style="padding: 1.25rem;">
              <h3 class="sed-card-title" style="display: flex; align-items: center; gap: 8px;"><span style="color: #e6273e; display: inline-flex;">${getDetectionSvg('gunshot', 20, '#e6273e')}</span> ${t('detectionTypes.gunshot')}</h3>
              <p class="sed-card-text">${t('detectionPages.gunshot.lead')}</p>
            </div>
          </div>
        </div>

        <!-- Glass Break -->
        <div class="sed-card" style="padding: 0; overflow: hidden; cursor: pointer; display: flex; flex-direction: column; justify-content: space-between;" data-route="detection-glass">
          <div>
            <div style="height: 180px; background: #f8fafc; padding: 1rem; text-align: center; border-bottom: 1px solid var(--slate-line); display: flex; align-items: center; justify-content: center;">
              <img src="/images/Detekce_Sklo.png" alt="${t('detectionTypes.glass')}" style="max-width: 100%; max-height: 160px; object-fit: contain;">
            </div>
            <div style="padding: 1.25rem;">
              <h3 class="sed-card-title" style="display: flex; align-items: center; gap: 8px;"><span style="color: var(--blue-primary); display: inline-flex;">${getDetectionSvg('glass', 20, 'var(--blue-primary)')}</span> ${t('detectionTypes.glass')}</h3>
              <p class="sed-card-text">${t('detectionPages.glass.lead')}</p>
            </div>
          </div>
        </div>

        <!-- Drone -->
        <div class="sed-card" style="padding: 0; overflow: hidden; cursor: pointer; display: flex; flex-direction: column; justify-content: space-between;" data-route="detection-drone">
          <div>
            <div style="height: 180px; background: #f8fafc; padding: 1rem; text-align: center; border-bottom: 1px solid var(--slate-line); display: flex; align-items: center; justify-content: center;">
              <img src="/images/Untitled-design-3-1024x679.png" alt="${t('detectionTypes.drone')}" style="max-width: 100%; max-height: 160px; object-fit: contain;">
            </div>
            <div style="padding: 1.25rem;">
              <h3 class="sed-card-title" style="display: flex; align-items: center; gap: 8px;"><span style="color: var(--navy-dark); display: inline-flex;">${getDetectionSvg('drone', 20, 'var(--navy-dark)')}</span> ${t('detectionTypes.drone')}</h3>
              <p class="sed-card-text">${t('detectionPages.drone.lead')}</p>
            </div>
          </div>
        </div>

        <!-- Scream -->
        <div class="sed-card" style="padding: 0; overflow: hidden; cursor: pointer; display: flex; flex-direction: column; justify-content: space-between;" data-route="detection-scream">
          <div>
            <div style="height: 180px; background: #f8fafc; padding: 1rem; text-align: center; border-bottom: 1px solid var(--slate-line); display: flex; align-items: center; justify-content: center;">
              <img src="/images/Detekce_Agrese.png" alt="${t('detectionTypes.scream')}" style="max-width: 100%; max-height: 160px; object-fit: contain;">
            </div>
            <div style="padding: 1.25rem;">
              <h3 class="sed-card-title" style="display: flex; align-items: center; gap: 8px;"><span style="color: #f59e0b; display: inline-flex;">${getDetectionSvg('scream', 20, '#f59e0b')}</span> ${t('detectionTypes.scream')}</h3>
              <p class="sed-card-text">${t('detectionPages.scream.lead')}</p>
            </div>
          </div>
        </div>
      </div>

      <div style="text-align: center; margin-top: 2.5rem;">
        <a href="#detection" class="sed-btn sed-btn-primary" data-route="detection">
          <span>${t('home.viewDetections')}</span>
        </a>
      </div>
    </div>
  `;
  container.appendChild(detectionCatalogSection);

  // 5. System Architecture Graph Section
  const deploymentSection = document.createElement('section');
  deploymentSection.className = 'sed-section';
  deploymentSection.style.backgroundColor = '#ffffff';
  deploymentSection.innerHTML = `
    <div class="sed-container">
      <div class="sed-section-header">
        <div class="sed-eyebrow">${t('home.flexibleArchEyebrow') || 'FLEXIBLE ARCHITECTURE'}</div>
        <h2 class="sed-section-title">${t('solutions.title')}</h2>
        <p class="sed-section-desc">${t('solutions.subtitle')}</p>
      </div>
      <div id="sed-architecture-graph-slot"></div>
    </div>
  `;
  const archSlot = deploymentSection.querySelector('#sed-architecture-graph-slot');
  if (archSlot) {
    const archGraph = renderArchitectureGraph(onNavigate);
    if (archGraph && archGraph instanceof Node) {
      archSlot.appendChild(archGraph);
    }
  }
  container.appendChild(deploymentSection);

  // 6. Testimonials Carousel
  const testimonials = renderTestimonialsCarousel();
  if (testimonials && testimonials instanceof Node) {
    container.appendChild(testimonials);
  }

  // 7. Customer & Partner Logo Carousel
  const customerCarousel = renderCustomerCarousel();
  if (customerCarousel && customerCarousel instanceof Node) {
    container.appendChild(customerCarousel);
  }

  
  // 8. Latest Insights, News & Case Studies Section
  const newsSection = document.createElement('section');
  newsSection.className = 'sed-section';
  newsSection.style.backgroundColor = '#ffffff';

  const lang = getLocale() || 'en';

  const newsData = {
    eyebrow: lang === 'cs' ? 'AKTUÁLNÍ DĚNÍ & PŘÍPADOVÉ STUDIE' : (lang === 'de' ? 'AKTUELLES & FALLSTUDIEN' : 'PRESS & LATEST INSIGHTS'),
    title: t('newsSection.title') || (lang === 'cs' ? 'Aktuality a tiskové zprávy' : 'Latest News & Press Releases'),
    subtitle: t('newsSection.subtitle') || (lang === 'cs' ? 'Sledujte nejnovější technologické inovace, televizní reportáže, ocenění a nasazení Sound Event Detectoru.' : 'Stay updated with the latest technological developments, TV reports, awards, and field deployment results.'),
    allNewsBtn: lang === 'cs' ? 'Všechny aktuality →' : 'All News Releases →',
    allCasesBtn: lang === 'cs' ? 'Případové studie →' : 'All Case Studies →',
    allProdsBtn: lang === 'cs' ? 'Produktové novinky →' : 'Product Updates →'
  };

  newsSection.innerHTML = `
    <div class="sed-container">
      <div class="sed-section-header">
        <div class="sed-eyebrow">${newsData.eyebrow}</div>
        <h2 class="sed-section-title">${newsData.title}</h2>
        <p class="sed-section-desc">${newsData.subtitle}</p>
      </div>

      <div id="sed-home-insights-grid" class="sed-grid-3" style="margin-bottom: 2.5rem;">
        <!-- Dynamically injected latest articles from all 3 categories -->
      </div>

      <!-- Navigation links to 3 full archive pages -->
      <div style="display: flex; justify-content: center; gap: 1rem; flex-wrap: wrap; border-top: 1px solid var(--slate-line); padding-top: 2rem;">
        <a href="#news" class="sed-btn sed-btn-primary" data-route="news" style="padding: 0.75rem 1.5rem; font-size: 0.92rem;">
          <span>${newsData.allNewsBtn}</span>
        </a>
        <a href="#case-studies" class="sed-btn sed-btn-outline" data-route="case-studies" style="padding: 0.75rem 1.5rem; font-size: 0.92rem; color: var(--navy-dark); border-color: var(--navy-dark);">
          <span>${newsData.allCasesBtn}</span>
        </a>
        <a href="#product-updates" class="sed-btn sed-btn-outline" data-route="product-updates" style="padding: 0.75rem 1.5rem; font-size: 0.92rem; color: var(--navy-dark); border-color: var(--navy-dark);">
          <span>${newsData.allProdsBtn}</span>
        </a>
      </div>

    </div>
  `;

  function renderInsightCards() {
    const gridEl = newsSection.querySelector('#sed-home-insights-grid');
    if (!gridEl) return;

    // 1. Latest News (category: 'latest-news' or 'news')
    const newsList = getArticlesByCategory('latest-news');
    const latestNews = (newsList && newsList.length > 0) ? newsList[0] : {
      slug: 'czech-technology-featured-in-an-american-tv-report',
      img: '/images/Curiosity-Lab-in-Atlanta-Tv-news-EN-title-1024x576.png'
    };

    // 2. Latest Case Study (category: 'case-studies')
    const caseList = getArticlesByCategory('case-studies');
    const latestCase = (caseList && caseList.length > 0) ? caseList[0] : {
      slug: 'sound-detection-improve-safety-cities',
      img: '/images/Muze-detekce-zvuku-zvysit-bezpecnost-ve-mestech_EN-2048x1152.png'
    };

    // 3. Latest Product Update (category: 'product-updates')
    const prodList = getArticlesByCategory('product-updates');
    const latestProd = (prodList && prodList.length > 0) ? prodList[0] : {
      slug: 'great-improvements-for-sed-app-users',
      img: '/images/SED_Novinky-aplikace_EN-2.png'
    };

    const cardConfigs = [
      {
        art: latestNews,
        type: 'news',
        badgeBg: 'rgba(53, 168, 223, 0.95)',
        defaultBadge: lang === 'cs' ? '📺 Aktuality a tisk' : '📺 Latest News',
        defaultBtn: lang === 'cs' ? 'Číst celou zprávu →' : 'Read Full Article →'
      },
      {
        art: latestCase,
        type: 'case-study',
        badgeBg: 'rgba(16, 185, 129, 0.95)',
        defaultBadge: lang === 'cs' ? '🏛️ Případová studie' : '🏛️ Case Study',
        defaultBtn: lang === 'cs' ? 'Prozkoumat studii →' : 'Explore Case Study →'
      },
      {
        art: latestProd,
        type: 'product-update',
        badgeBg: 'rgba(99, 102, 241, 0.95)',
        defaultBadge: lang === 'cs' ? '🚀 Produktová novinka' : '🚀 Product Update',
        defaultBtn: lang === 'cs' ? 'Zobrazit novinku →' : 'Read Product Update →'
      }
    ];

    gridEl.innerHTML = cardConfigs.map(({ art, type, badgeBg, defaultBadge, defaultBtn }) => {
      const slug = art.slug || 'article';
      const trans = (art.translations && art.translations[lang]) || (articleIndexTranslations[lang] && articleIndexTranslations[lang][slug]) || {};
      const title = trans.title || art.title || slug.replace(/-/g, ' ').toUpperCase();
      const desc = trans.excerpt || art.excerpt || (art.content ? art.content.replace(/<[^>]+>/g, '').slice(0, 160) + '...' : 'Sound Event Detector latest update.');
      const badge = trans.category || art.tag || defaultBadge;
      const img = art.img || '/images/prumyslova-bezpecnost.png';

      return `
        <div class="sed-card sed-insight-card" style="cursor: pointer; display: flex; flex-direction: column; justify-content: space-between; overflow: hidden; padding: 0; border: 1px solid var(--slate-line); border-radius: 14px; transition: transform 0.2s ease, box-shadow 0.2s ease;" data-article-id="${slug}">
          <div>
            <div style="height: 180px; overflow: hidden; position: relative; background: var(--navy-dark);">
              <img src="${img}" alt="${title}" style="width: 100%; height: 100%; object-fit: cover;">
              <div style="position: absolute; top: 12px; left: 12px; background: ${badgeBg}; color: #ffffff; padding: 4px 10px; border-radius: 6px; font-size: 0.75rem; font-weight: 800; text-transform: uppercase; letter-spacing: 0.5px;">
                ${badge}
              </div>
              ${art.date ? `
                <div style="position: absolute; bottom: 10px; right: 12px; background: rgba(10, 25, 47, 0.85); color: #ffffff; padding: 0.2rem 0.55rem; border-radius: 4px; font-size: 0.72rem; font-weight: 700; backdrop-filter: blur(4px);">
                  ${art.date}
                </div>
              ` : ''}
            </div>
            <div style="padding: 1.25rem 1.25rem 0.5rem;">
              <h3 class="sed-card-title" style="font-size: 1.15rem; line-height: 1.4; margin-bottom: 0.6rem; color: var(--navy-dark);">
                ${title}
              </h3>
              <p class="sed-card-text" style="font-size: 0.9rem; line-height: 1.6; color: var(--text-dark);">
                ${desc}
              </p>
            </div>
          </div>
          <div style="padding: 0 1.25rem 1.25rem;">
            <button class="sed-btn sed-btn-outline" style="width: 100%; color: var(--blue-primary); border-color: var(--blue-primary); font-size: 0.88rem; font-weight: 700;">
              ${defaultBtn}
            </button>
          </div>
        </div>
      `;
    }).join('');

    gridEl.querySelectorAll('.sed-insight-card').forEach(card => {
      card.addEventListener('mouseenter', () => {
        card.style.transform = 'translateY(-4px)';
        card.style.boxShadow = '0 14px 30px rgba(0,0,0,0.1)';
      });
      card.addEventListener('mouseleave', () => {
        card.style.transform = 'none';
        card.style.boxShadow = 'none';
      });
      card.addEventListener('click', () => {
        const artId = card.getAttribute('data-article-id');
        if (artId) openArticleModal(artId);
      });
    });
  }

  // Initial render
  renderInsightCards();

  // Reactive re-render on article updates
  subscribeArticleStore(() => {
    renderInsightCards();
  });

  container.appendChild(newsSection);

  // 8. Direct Technical Consultation Form Section
  const consultSection = document.createElement('section');
  consultSection.className = 'sed-section-alt';
  consultSection.style.padding = '4.5rem 0';
  consultSection.innerHTML = `
    <div class="sed-container">
      <div class="sed-card" style="max-width: 800px; margin: 0 auto; padding: 2.5rem; background: #ffffff; border: 1px solid var(--slate-line); box-shadow: 0 15px 35px rgba(0,0,0,0.06);">
        <div style="text-align: center; margin-bottom: 2rem;">
          <div class="sed-eyebrow" style="margin-bottom: 0.5rem;">${t('home.consultEyebrow') || 'DIRECT ENGINEERING CONSULTATION'}</div>
          <h2 class="sed-section-title" style="margin-bottom: 0.6rem;">
            ${t('home.consultTitle')}
          </h2>
          <p style="color: var(--text-muted); font-size: 0.95rem; line-height: 1.6; max-width: 600px; margin: 0 auto;">
            ${t('home.consultDesc')}
          </p>
        </div>

        <div id="home-form-alert" style="display: none; margin-bottom: 1.25rem; padding: 1rem 1.25rem; border-radius: var(--radius-unified); font-size: 0.92rem; line-height: 1.5;"></div>

        <form id="sed-home-contact-form" style="display: flex; flex-direction: column; gap: 1.2rem;">
          <input type="text" name="_gotcha" style="display:none !important;" tabindex="-1" autocomplete="off">

          <div class="sed-form-grid-2">
            <div>
              <label style="font-size: 0.85rem; font-weight: 700; color: var(--navy-dark); display: block; margin-bottom: 0.4rem;">${t('home.nameLabel')}</label>
              <input type="text" id="home-name" placeholder="${t('home.namePlaceholder') || 'John Doe'}" required style="width: 100%; padding: 0.75rem 1rem; border: 1px solid var(--slate-line); border-radius: var(--radius-unified); outline: none;">
            </div>
            <div>
              <label style="font-size: 0.85rem; font-weight: 700; color: var(--navy-dark); display: block; margin-bottom: 0.4rem;">${t('home.companyLabel')}</label>
              <input type="text" id="home-company" placeholder="${t('home.companyPlaceholder') || 'e.g. City of Pilsen, Securitas'}" required style="width: 100%; padding: 0.75rem 1rem; border: 1px solid var(--slate-line); border-radius: var(--radius-unified); outline: none;">
            </div>
          </div>

          <div>
            <label style="font-size: 0.85rem; font-weight: 700; color: var(--navy-dark); display: block; margin-bottom: 0.4rem;">${t('home.emailLabel')}</label>
            <input type="email" id="home-email" placeholder="${t('home.emailPlaceholder') || 'john.doe@organization.com'}" required style="width: 100%; padding: 0.75rem 1rem; border: 1px solid var(--slate-line); border-radius: var(--radius-unified); outline: none;">
          </div>

          <div>
            <label style="font-size: 0.85rem; font-weight: 700; color: var(--navy-dark); display: block; margin-bottom: 0.4rem;">${t('home.facilityLabel')}</label>
            <textarea id="home-message" rows="4" placeholder="${t('home.facilityPlaceholder') || 'Describe your facility & requirements...'}" required style="width: 100%; padding: 0.75rem 1rem; border: 1px solid var(--slate-line); border-radius: var(--radius-unified); outline: none; font-family: var(--font-body);"></textarea>
          </div>

          <button type="submit" id="home-submit-btn" class="sed-btn sed-btn-primary" style="height: 48px; font-size: 1rem; font-weight: 700; display: flex; align-items: center; justify-content: center; gap: 8px;">
            <span>${t('home.submitConsult')}</span>
          </button>
          <div style="font-size: 0.78rem; color: var(--text-light); text-align: center;">${t('home.consultNotice')}</div>
        </form>
      </div>
    </div>
  `;
  container.appendChild(consultSection);

  // Attach Home Form Handler
  const homeForm = container.querySelector('#sed-home-contact-form');
  const homeAlert = container.querySelector('#home-form-alert');
  const homeBtn = container.querySelector('#home-submit-btn');

  if (homeForm) {
    homeForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const name = homeForm.querySelector('#home-name')?.value || '';
      const company = homeForm.querySelector('#home-company')?.value || '';
      const email = homeForm.querySelector('#home-email')?.value || '';
      const message = homeForm.querySelector('#home-message')?.value || '';
      const gotcha = homeForm.querySelector('input[name="_gotcha"]')?.value || '';

      if (gotcha) {
        homeForm.reset();
        return;
      }

      homeBtn.disabled = true;
      homeBtn.innerHTML = `
        <span class="sed-spinner" style="display: inline-block; width: 18px; height: 18px; border: 2px solid rgba(255,255,255,0.3); border-top-color: #ffffff; border-radius: 50%; animation: spin 0.8s linear infinite;"></span>
        <span>${t('contactPage.sending')}</span>
      `;
      homeAlert.style.display = 'none';

      try {
        const response = await fetch('/api/contact', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name,
            company,
            email,
            message,
            source: 'Home Direct Consultation Form'
          })
        });

        const data = await response.json();
        if (response.ok && data.success) {
          homeAlert.style.display = 'block';
          homeAlert.style.background = '#ecfdf5';
          homeAlert.style.border = '1px solid #10b981';
          homeAlert.style.color = '#065f46';
          homeAlert.innerHTML = `
            <div style="font-weight: 700; margin-bottom: 4px;">✅ ${t('contactPage.successTitle')}</div>
            <div>${t('home.consultSuccess')}</div>
          `;
          homeForm.reset();
        } else {
          throw new Error(data.error || 'Submission failed');
        }
      } catch (err) {
        console.error('Consultation Form Error:', err);
        homeAlert.style.display = 'block';
        homeAlert.style.background = '#fef2f2';
        homeAlert.style.border = '1px solid #ef4444';
        homeAlert.style.color = '#991b1b';
        homeAlert.innerHTML = `
          <div style="font-weight: 700; margin-bottom: 4px;">⚠️ ${t('contactPage.errorTitle')}</div>
          <div>${t('home.consultFailed')}</div>
        `;
      } finally {
        homeBtn.disabled = false;
        homeBtn.innerHTML = `<span>${t('home.submitConsult')}</span>`;
      }
    });
  }

  // Handle SPA Route Clicks
  container.querySelectorAll('[data-route]').forEach(link => {
    link.addEventListener('click', (e) => {
      e.preventDefault();
      const targetRoute = link.getAttribute('data-route');
      if (onNavigate) onNavigate(targetRoute);
    });
  });

  return container;
}
